/**
 * TTS Flow — text-to-speech automation builder. Templates 7/8/9 cover
 * Simple TTS / TTS+DTMF / TTS+Call Patch respectively. The backend wire
 * format is several nested JSON-stringified payloads — see payload.ts
 * for the build/hydrate functions.
 */

/** Template ids the legacy backend understands. Must remain string-typed. */
export type TtsTemplateId = '7' | '8' | '9';

export interface TtsTemplateOption {
  value: TtsTemplateId;
  label: string;
  description: string;
}

export const TTS_TEMPLATES: TtsTemplateOption[] = [
  {
    value: '7',
    label: 'TTS Simple',
    description: 'Play a TTS prompt — no DTMF, no patch.',
  },
  {
    value: '8',
    label: 'TTS + DTMF',
    description:
      'Play a TTS prompt, accept a DTMF key from the caller, branch.',
  },
  {
    value: '9',
    label: 'TTS + Call Patch',
    description:
      'Play a TTS prompt, accept DTMF, transfer to an agent group on success.',
  },
];

/**
 * Wire-format row type. Only `prompt` and `variable` make it to the
 * backend — see auto-synthesize logic in payload.ts for how editor-only
 * `text` rows are promoted into `prompt` rows at save time.
 */
export type TtsRowType = 'prompt' | 'variable';

/**
 * Editor-only mode used by the two-pane builder. `text` rows do NOT exist
 * on the wire — they're auto-synthesized to a prompt before submit.
 */
export type TtsEditorMode = 'prompt' | 'text' | 'variable';

export interface TtsRow {
  ttsType: TtsRowType;
  /** Prompt → promptId (string). Variable → slot number (1–10). */
  value: string;
  /** Variable rows only — the tts_type string (e.g. "number", "text"). */
  variableType: string;
  /**
   * Editor-only: when set, this row was authored as inline text and will
   * be synthesized into a prompt at save time. Wire-time payload.ts
   * strips this field so the JSON the backend sees stays exactly as it
   * was in the legacy app.
   */
  text?: string;
  /**
   * Editor-only: optional cached preview URL after the user clicks the
   * ▶ preview on a `text` row. Lets us play it back without re-spending
   * credits on /convertion/texttospeech. Stripped at save time.
   */
  previewUrl?: string;
  /**
   * Editor-only: when a text row has been previewed, we hold the ttsId
   * returned by /convertion/texttospeech so the save step can finalize
   * that pending row instead of re-synthesizing from scratch.
   */
  previewTtsId?: number;
}

/** True when the editor row should be rendered as a text-mode card. */
export function isEditorTextRow(r: TtsRow): boolean {
  return typeof r.text === 'string' && r.text.length > 0 && r.ttsType !== 'variable';
}

export type Gender = 'MALE' | 'FEMALE';

export interface TtsPatchRow {
  groupId: string;
  groupName: string;
  agentDtmf: string;
}

/* ---------------- List row ---------------- */

/* ---------------- TTS Logs (continuous logs) ---------------- */

/** Row from POST /api/obd/get/continuous/logs. */
export interface TtsLogRow {
  id: number;
  campaignId?: number | string;
  uniqueKey?: string;
  requestDate?: string;
  successCount?: number;
  failedCount?: number;
  dndCount?: number;
  totalNos?: number;
  /** JSON-serialized payload string the runner submitted. */
  requestPayload?: string;
}

export interface TtsLogRequest {
  userId: number | string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
}

export interface TtsFlowListRow {
  id: number;
  ttsFlowName: string;
  templateId: string;
  status: string;
  startDate?: string;
  endDate?: string;
  createdAt?: string;
  updatedAt?: string;
}

/* ---------------- GET-detail wire shape ---------------- */

/** Raw response from GET /api/obd/get/tts/flow/{id} — all the nested JSON
 *  blobs come back as strings and need parsing. */
export interface TtsFlowDetailResponse {
  id: number;
  ttsFlowName: string;
  templateId: string;
  /** JSON string of `{ language, gender, ttsRows: { ttsList: [...] } }`. */
  ttsFlow: string;
  /** JSON string of menu/dtmf/voice-file ids + wait/re-prompt. */
  templateData: string;
  /** JSON string of SMS triggers + webhook config. */
  smsData: string;
  /** JSON string of `{ patchList: [...] }` or empty string. */
  agentRows: string;
  ivrId?: string;
  startDate?: string;
  endDate?: string;
  startTime?: string;
  endTime?: string;
  campaignIds?: string;
}

/* ---------------- Compose/update request (wire format) ---------------- */

/**
 * POST /api/obd/add/tts/flow body. Identical to update except `id`. The
 * three big nested objects are stringified before being attached.
 */
export interface TtsFlowComposeRequest {
  userId: number | string;
  templateId: TtsTemplateId | '';
  ttsFlowName: string;
  /** JSON-stringified `{ language, gender, ttsRows: { ttsList: [...] } }`. */
  ttsFlow: string;
  /** JSON-stringified `{ dtmf, welcomePId, menuPId, noInputPId, ... }`. */
  templateData: string;
  /** JSON-stringified `{ smsSuccessApi, smsFailApi, ... }`. */
  smsData: string;
  /** JSON-stringified `{ patchList: [...] }` or empty string. */
  agentRows: string;
  ivrId: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
}

export interface TtsFlowUpdateRequest extends TtsFlowComposeRequest {
  id: number;
}

/* ---------------- Form values (UI state) ---------------- */

/**
 * In-form state for the wizard. The wire-format conversion lives in
 * payload.ts (buildTtsFlowRequest / hydrateTtsFlowFromDetail).
 */
export interface TtsFlowFormValues {
  /** Pre-creation: '' means template not chosen yet. */
  templateId: TtsTemplateId | '';
  ttsFlowName: string;

  /** Language code from /api/obd/user/languages, e.g. "en-IN". */
  language: string;
  gender: Gender | '';

  /** TTS rows the user has built (prompt OR variable). */
  ttsRows: TtsRow[];

  /** Optional voice-file toggles + ids — only used by template 8/9. */
  useWelcomeFile: boolean;
  useNoinputFile: boolean;
  useWronginputFile: boolean;
  useThanksFile: boolean;
  useNoAgentFile: boolean;

  welcomeId: string;
  menuId: string;
  noInputId: string;
  wrongInputId: string;
  thanksId: string;
  noAgentId: string;

  /** Accepted DTMF digits, joined as comma-string at wire time. */
  dtmf: string[];
  menuWaitTime: string;
  rePrompt: string;

  /** Template 9 only — patch agent groups by DTMF. */
  patchList: TtsPatchRow[];

  /** SMS triggers — each holds either a pasted URL or a webhook id string. */
  sendSmsSuccess: boolean;
  smsApiSuccess: string;
  callDurationSMS: number;

  sendSmsFail: boolean;
  smsApiFail: string;

  sendSmsDtmf: boolean;
  smsApiDtmf: string;

  /** Template 9 only — patch success / fail SMS. */
  sendSmsPatchSuccess: boolean;
  callPatchSuccessMessage: string;
  sendSmsPatchFail: boolean;
  callPatchFailMessage: string;

  /** Toggle for webhook events on the campaign as a whole. */
  sendWebhook: boolean;
  webhookId: string;

  /** Optional linked Custom IVR. */
  useIvr: boolean;
  ivrId: string;

  /** Schedule window. */
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  startTime: string; // HH:MM
  endTime: string; // HH:MM
}

export const DEFAULT_TTS_FORM_VALUES: TtsFlowFormValues = {
  templateId: '',
  ttsFlowName: '',
  language: '',
  gender: '',
  ttsRows: [],
  useWelcomeFile: false,
  useNoinputFile: false,
  useWronginputFile: false,
  useThanksFile: false,
  useNoAgentFile: false,
  welcomeId: '',
  menuId: '',
  noInputId: '',
  wrongInputId: '',
  thanksId: '',
  noAgentId: '',
  dtmf: [],
  menuWaitTime: '',
  rePrompt: '',
  patchList: [],
  sendSmsSuccess: false,
  smsApiSuccess: '',
  callDurationSMS: 0,
  sendSmsFail: false,
  smsApiFail: '',
  sendSmsDtmf: false,
  smsApiDtmf: '',
  sendSmsPatchSuccess: false,
  callPatchSuccessMessage: '',
  sendSmsPatchFail: false,
  callPatchFailMessage: '',
  sendWebhook: false,
  webhookId: '',
  useIvr: false,
  ivrId: '',
  startDate: '',
  endDate: '',
  startTime: '',
  endTime: '',
};

/* ---------------- Template-feature flags ---------------- */

export interface TtsTemplateFeatures {
  /** Template 8 + 9 — collect DTMF input. */
  hasDtmf: boolean;
  /** Template 9 — patch list to agent groups. */
  hasPatch: boolean;
  /** Template 8 + 9 — menu prompt + wait + re-prompt + optional aux files. */
  hasVoiceFiles: boolean;
  /** Template 8 + 9 — SMS on DTMF trigger available. */
  hasSmsDtmf: boolean;
  /** Template 9 — patch SMS triggers. */
  hasPatchSms: boolean;
  /** Template 9 — requires the no-agent voice file. */
  needsNoAgent: boolean;
}

export function templateFeatures(t: TtsTemplateId | ''): TtsTemplateFeatures {
  const dtmf = t === '8' || t === '9';
  const patch = t === '9';
  return {
    hasDtmf: dtmf,
    hasPatch: patch,
    hasVoiceFiles: dtmf,
    hasSmsDtmf: dtmf,
    hasPatchSms: patch,
    needsNoAgent: patch,
  };
}

export function templateLabel(t: string | TtsTemplateId | ''): string {
  return TTS_TEMPLATES.find((opt) => opt.value === t)?.label ?? String(t || '');
}
